<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">💾</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
          <template slot-scope="{ row }" slot="dataTime">
            {{ formatTime(row.dataTime) }}
          </template>
          
          <!-- 使用率进度条 -->
          <template slot-scope="{ row }" slot="usageRate">
            <div class="usage-progress-wrapper">
              <div class="progress-bar-container">
                <div 
                  class="progress-bar" 
                  :class="getUsageClass(row.usageRate)"
                  :style="{ width: (parseFloat(row.usageRate) * 100) + '%' }"
                >
                </div>
              </div>
              <span class="usage-text" :class="getUsageClass(row.usageRate)">
                {{ (parseFloat(row.usageRate) * 100).toFixed(2) }}%
              </span>
            </div>
          </template>
          
          <!-- 内存容量 -->
          <template slot-scope="{ row }" slot="capacity">
            <div class="capacity-info">
              <div class="capacity-item">
                <span class="label">{{ langMap.dashboard_memoryTotal[lang] }}:</span>
                <span class="value">{{ row.total }} GB</span>
              </div>
              <div class="capacity-item">
                <span class="label">{{ langMap.dashboard_memoryUsed[lang] }}:</span>
                <span class="value usage">{{ row.used }} GB</span>
              </div>
              <div class="capacity-item">
                <span class="label">{{ langMap.dashboard_memoryFree[lang] }}:</span>
                <span class="value free">{{ row.free }} GB</span>
              </div>
            </div>
          </template>

          <!-- 内存详情 -->
          <template slot-scope="{ row }" slot="details">
            <div class="memory-details-grid">
              <div class="detail-item">
                <span class="detail-label">{{ langMap.dashboard_memoryBuffer[lang] }}</span>
                <span class="detail-value">{{ row.buffer }} GB</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ langMap.dashboard_memoryCached[lang] }}</span>
                <span class="detail-value">{{ row.cached }} GB</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ langMap.dashboard_memoryUser[lang] }}</span>
                <span class="detail-value">{{ row.user }} GB</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ langMap.dashboard_memoryShared[lang] }}</span>
                <span class="detail-value">{{ row.shared }} GB</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ langMap.dashboard_memoryLocked[lang] }}</span>
                <span class="detail-value">{{ row.locked }} GB</span>
              </div>
            </div>
          </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'MemoryHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: [
        {
          title: langMap.dashboard_dataTime[this.$store.state.app.language],
          slot: 'dataTime',
          width: 180,
          align: 'center'
        },
        {
          title: langMap.dashboard_memoryUsageRate[this.$store.state.app.language],
          slot: 'usageRate',
          width: 220,
          align: 'center'
        },
        {
          title: langMap.dashboard_memoryCapacity[this.$store.state.app.language],
          slot: 'capacity',
          width: 280,
          align: 'left'
        },
        {
          title: langMap.dashboard_memoryDetails[this.$store.state.app.language],
          slot: 'details',
          minWidth: 400,
          align: 'left'
        },
        {
          title: langMap.dashboard_robotCode[this.$store.state.app.language],
          key: 'robotCode',
          width: 150,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    lang() {
      this.updateColumnsTitle();
    }
  },
  methods: {
    updateColumnsTitle() {
      this.columns[0].title = this.langMap.dashboard_dataTime[this.lang];
      this.columns[1].title = this.langMap.dashboard_memoryUsageRate[this.lang];
      this.columns[2].title = this.langMap.dashboard_memoryCapacity[this.lang];
      this.columns[3].title = this.langMap.dashboard_memoryDetails[this.lang];
      this.columns[4].title = this.langMap.dashboard_robotCode[this.lang];
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getUsageClass(usageRate) {
      const u = parseFloat(usageRate) * 100;
      if (u >= 90) return 'high';
      if (u >= 70) return 'medium';
      return 'normal';
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size);
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  // 使用率进度条
  .usage-progress-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 8px;
    
    .progress-bar-container {
      flex: 1;
      height: 20px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
      border: 1px solid rgba(0, 240, 255, 0.2);
      
      .progress-bar {
        height: 100%;
        border-radius: 10px;
        transition: all 0.3s ease;
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shimmer 2s infinite;
        }
        
        &.high {
          background: linear-gradient(90deg, #ff4d4f 0%, #ff7875 100%);
        }
        
        &.medium {
          background: linear-gradient(90deg, #faad14 0%, #ffc53d 100%);
        }
        
        &.normal {
          background: linear-gradient(90deg, #52c41a 0%, #73d13d 100%);
        }
      }
    }
    
    .usage-text {
      font-family: 'Courier New', monospace;
      font-weight: 700;
      font-size: 13px;
      min-width: 60px;
      text-align: right;
      
      &.high { color: #ff4d4f; }
      &.medium { color: #faad14; }
      &.normal { color: #52c41a; }
    }
  }
  
  // 容量信息
  .capacity-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 0;
    
    .capacity-item {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .label {
        color: rgba(255, 255, 255, 0.6);
        font-size: 12px;
        min-width: 60px;
      }
      
      .value {
        font-family: 'Courier New', monospace;
        font-weight: 600;
        font-size: 13px;
        color: #fff;
        
        &.usage {
          color: #faad14;
        }
        
        &.free {
          color: #52c41a;
        }
      }
    }
  }
  
  // 内存详情网格
  .memory-details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    padding: 6px 0;
    
    .detail-item {
      display: flex;
      flex-direction: column;
      padding: 6px 10px;
      background: rgba(0, 240, 255, 0.05);
      border: 1px solid rgba(0, 240, 255, 0.2);
      border-radius: 4px;
      
      .detail-label {
        color: rgba(255, 255, 255, 0.6);
        font-size: 11px;
        margin-bottom: 4px;
      }
      
      .detail-value {
        color: #00f0ff;
        font-family: 'Courier New', monospace;
        font-weight: 600;
        font-size: 13px;
      }
    }
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
